import React from "react";
import styled from "styled-components";
import HeroVideo from "../../assets/heroVideo.mp4";
import {useNavigate} from "react-router-dom";

const HeroSection = () => {
  const navigate = useNavigate();
  return (
    <TheHeroSection>
      <div className="hero-video">
        <video src={HeroVideo} autoPlay loop muted playsInline />
      </div>
      <div className="hero-overlay" />
      <div className="hero-content">
        <div className="hero-container">
          <h1>
            Your Global
            <span className="highlight"> Shipping Partner</span>
          </h1>

          <div className="stat-badge">
            <span className="year">Since 1969</span>
            <span className="separator">•</span>
            <span className="location">NYC Headquarters</span>
          </div>

          <div className="hero-text">
            <p>
              <strong>Trans World Shipping Company</strong> stands as one of the
              largest NVO carriers in the international trade lane. We're not
              just a logistics provider — we're the name businesses trust for
              full-service distribution.
            </p>

            <p>
              Founded in <strong>1969 in Hamburg, Germany</strong>, we've grown
              into a pacesetter across global trade routes. From cost leadership
              to transport innovation, we deliver what matters most:{" "}
              <strong>reliability, speed, and competitive advantage.</strong>
            </p>

            <div className="value-props">
              <span>✓ 40+ Years Experience</span>
              <span>✓ Global Network</span>
              <span>✓ 24/7 Support</span>
            </div>
          </div>

          <div className="cta-group">
            <button className="btn-primary" onClick={() => navigate("/contact")}>
              Get a Quote
            </button>
            <button className="btn-secondary" onClick={() => navigate("/track-shipment")}>
              Track Shipment
            </button>
          </div>

          <div className="welcome-message">
            <span className="welcome-text">Welcome Aboard</span>
            <span className="anchor-icon">⚓</span>
          </div>
        </div>
      </div>
    </TheHeroSection>
  );
};

const TheHeroSection = styled.section`
  width: 100%;
  min-height: 90vh;
  position: relative;
  background: #0a0f1a;
  overflow: hidden;

  .hero-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .hero-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(0, 20, 40, 0.75) 50%,
      rgba(0, 0, 0, 0.85) 100%
    );
    z-index: 1;
  }

  .hero-content {
    position: relative;
    z-index: 2;
    width: 100%;
    min-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6rem 2rem;
  }

  .hero-container {
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
  }

  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 1.5rem;
    line-height: 1.2;

    .highlight {
      color: #d4af37;
      display: inline-block;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 60px;
        height: 3px;
        background: #d4af37;
      }
    }
  }

  .stat-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    background: rgba(212, 175, 55, 0.15);
    backdrop-filter: blur(10px);
    padding: 0.5rem 1.25rem;
    border-radius: 40px;
    margin-bottom: 2rem;
    font-size: 0.9rem;
    font-weight: 500;
    border: 1px solid rgba(212, 175, 55, 0.3);

    .year,
    .location {
      color: #d4af37;
    }

    .separator {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .hero-text {
    max-width: 60%;
    margin-bottom: 2.5rem;

    p {
      color: rgba(255, 255, 255, 0.9);
      font-size: 1rem;
      line-height: 1.7;
      margin-bottom: 1.25rem;

      strong {
        color: #d4af37;
        font-weight: 600;
      }
    }
  }

  .value-props {
    display: flex;
    gap: 2rem;
    margin-top: 1.5rem;
    flex-wrap: wrap;

    span {
      color: white;
      font-size: 0.9rem;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &::before {
        content: "★";
        color: #d4af37;
        font-size: 1rem;
      }
    }
  }

  .cta-group {
    display: flex;
    gap: 1rem;
    margin-bottom: 2.5rem;
    flex-wrap: wrap;

    button {
      padding: 0.9rem 2rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.25s ease;
      font-family: inherit;
    }

    .btn-primary {
      background: #d4af37;
      border: none;
      color: #0b1e33;

      &:hover {
        background: #c4a12e;
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
      }
    }

    .btn-secondary {
      background: transparent;
      border: 1.5px solid #d4af37;
      color: #d4af37;

      &:hover {
        background: rgba(212, 175, 55, 0.1);
        transform: translateY(-2px);
      }
    }
  }

  .welcome-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(212, 175, 55, 0.3);
    width: fit-content;

    .welcome-text {
      color: #d4af37;
      font-size: 1rem;
      font-weight: 500;
      letter-spacing: 1px;
    }

    .anchor-icon {
      font-size: 1.2rem;
      animation: sway 3s ease-in-out infinite;
      display: inline-block;
    }
  }

  @keyframes sway {
    0%,
    100% {
      transform: rotate(0deg);
    }
    50% {
      transform: rotate(5deg);
    }
  }

  /* Responsive Breakpoints */
  @media screen and (max-width: 1100px) {
    .hero-text {
      max-width: 70%;
    }
  }

  @media screen and (max-width: 900px) {
    .hero-content {
      padding: 5rem 1.75rem;
    }

    h1 {
      font-size: 2.8rem;
    }

    .hero-text {
      max-width: 80%;
    }
  }

  @media screen and (max-width: 768px) {
    min-height: 85vh;

    .hero-content {
      padding: 4rem 1.5rem;
      min-height: 85vh;
      align-items: flex-start;
    }

    h1 {
      font-size: 2.2rem;
      margin-bottom: 1.25rem;

      .highlight::after {
        width: 45px;
        bottom: -5px;
      }
    }

    .stat-badge {
      margin-bottom: 1.5rem;
      font-size: 0.8rem;
    }

    .hero-text {
      max-width: 100%;
      margin-bottom: 2rem;

      p {
        font-size: 0.95rem;
        line-height: 1.6;
      }
    }

    .value-props {
      gap: 1.25rem;

      span {
        font-size: 0.85rem;
      }
    }

    .cta-group button {
      padding: 0.75rem 1.5rem;
      font-size: 0.9rem;
    }
  }

  @media screen and (max-width: 550px) {
    .hero-content {
      padding: 3rem 1.25rem;
    }

    h1 {
      font-size: 1.8rem;
    }

    .stat-badge {
      padding: 0.4rem 1rem;
      margin-bottom: 1.25rem;
    }

    .hero-text p {
      font-size: 0.9rem;
    }

    .value-props {
      flex-direction: column;
      gap: 0.75rem;
      align-items: flex-start;
    }

    .cta-group {
      flex-direction: column;
      width: 100%;

      button {
        width: 100%;
        text-align: center;
      }
    }

    .welcome-message {
      margin-top: 0.5rem;

      .welcome-text {
        font-size: 0.9rem;
      }
    }
  }

  @media screen and (max-width: 480px) {
    h1 {
      font-size: 1.6rem;
    }

    .hero-text p {
      font-size: 0.85rem;
    }
  }
`;

export default HeroSection;
